import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";

/**
 * PlatformContext — hydrates platform-level branding + specialty registry from
 * the backend on mount. Rendered above AuthProvider so the login page can
 * already know the platform name / logo / tagline before the user has a token.
 *
 * Falls back to sensible defaults if the API is unreachable so the app never
 * hard-fails just because the backend is down.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BRANDING = {
  name: "Med Nexus",
  short_name: "Med Nexus",
  tagline: "Multi-Specialty AI Clinical Intelligence Platform",
  logo_url: "",
  primary_color: "#4ADE80",
  support_email: "",
};

const PlatformContext = createContext(null);

export function PlatformProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [specialties, setSpecialties] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/platform/config`, { timeout: 5000 });
        if (cancelled) return;
        setBranding({ ...DEFAULT_BRANDING, ...(data.branding || {}) });
        setSpecialties(Array.isArray(data.specialties) ? data.specialties : []);
      } catch {
        // Non-fatal — keep defaults, log to console for dev visibility.
        if (typeof console !== "undefined") {
          console.warn("[Platform] Failed to load /api/platform/config — using defaults");
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Push branding into <title> so the tab reflects the configured name.
  useEffect(() => {
    if (branding?.name) {
      const tagline = branding.tagline ? ` — ${branding.tagline}` : "";
      document.title = `${branding.name}${tagline}`;
    }
  }, [branding]);

  const value = useMemo(() => ({ branding, specialties, loaded }), [branding, specialties, loaded]);
  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatform() {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    // Return safe defaults if the hook is called outside the provider.
    // Components should still work; they just won't get live config.
    return { branding: DEFAULT_BRANDING, specialties: [], loaded: false };
  }
  return ctx;
}
